import React, { useState, useEffect } from 'react';
import { EventDetailRead, CompetitionClassRead, Country } from '../types/api';
import { apiService } from '../services/api';
import { urlParams } from '../utils/urlParams';
import { getCircleFlagUrl, resolveCountryName } from '../utils/countryFlag';
import { MapPin, Calendar, Ruler, Users, Layers, AlertTriangle, Trophy } from 'lucide-react';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatPrizeSum(value: number | null): string {
  if (value == null || value === 0) return '';
  return value.toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

interface EventDetailViewProps {
  eventId: string;
  onNavigateBack: () => void;
  onTitleChange?: (title: string | null) => void;
}

export const EventDetailView: React.FC<EventDetailViewProps> = ({
  eventId,
  onNavigateBack,
  onTitleChange,
}) => {
  const [event, setEvent] = useState<EventDetailRead | null>(null);
  const [classes, setClasses] = useState<CompetitionClassRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);

  useEffect(() => {
    apiService.getCountries().then(setCountries).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      apiService.getEventDetails(eventId),
      apiService.getEventClasses(eventId),
    ])
      .then(([ev, cls]) => {
        if (cancelled) return;
        setEvent(ev);
        setClasses(cls);
        onTitleChange?.('Event Details');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load event');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [eventId, onTitleChange]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#132B64]" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-4">
        <AlertTriangle size={48} className="text-red-400" />
        <p className="text-gray-700 text-center">{error || 'Event not found'}</p>
        <button onClick={onNavigateBack} className="text-sm text-[#132B64] underline">Go back</button>
      </div>
    );
  }

  const flagUrl = event.unified_location ? getCircleFlagUrl(event.unified_location) : null;
  const countryName = event.unified_location ? resolveCountryName(event.unified_location, countries) : null;
  const currencySymbols: Record<string, string> = {
    EUR: '€', USD: '$', GBP: '£', CHF: 'CHF ', AUD: 'A$', CAD: 'C$',
  };

  const prizeTotals = [
    { currency: 'EUR', value: event.total_prizes_eur_sum },
    { currency: 'USD', value: event.total_prizes_usd_sum },
    { currency: 'GBP', value: event.total_prizes_gbp_sum },
    { currency: 'CHF', value: event.total_prizes_chf_sum },
    { currency: 'AUD', value: event.total_prizes_aud_sum },
    { currency: 'CAD', value: event.total_prizes_cad_sum },
  ].filter(p => p.value != null && p.value > 0);

  return (
    <div className="min-h-screen bg-white">
      {/* Event info banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <div className="max-w-[320px] lg:max-w-[500px] mx-auto">
          {/* Title with flag */}
          <div className="flex items-center gap-3 mb-3">
            {flagUrl && (
              <img
                src={flagUrl}
                alt={countryName || ''}
                className="w-14 h-14 rounded-full shrink-0"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-[#132B64] leading-tight">
                {event.unified_competition_name || 'Event'}
              </h1>
              {event.unified_event_code && (
                <span className="inline-block mt-1 px-2.5 py-0.5 bg-[#132B64]/10 text-[#132B64] text-xs font-semibold rounded-full">
                  {event.unified_event_code}
                </span>
              )}
            </div>
          </div>

          {/* Metadata chips */}
          <div className="flex flex-wrap gap-2 mb-2">
            {event.unified_competition_date && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-700">
                <Calendar size={13} />
                {formatDate(event.class_date_min)}
                {event.class_date_max && event.class_date_min !== event.class_date_max && (
                  <> – {formatDate(event.class_date_max)}</>
                )}
              </span>
            )}
            {countryName && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-700">
                <MapPin size={13} />
                {countryName}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-700">
              <Layers size={13} />
              {event.num_classes} {event.num_classes === 1 ? 'class' : 'classes'}
            </span>
            {event.total_entries != null && event.total_entries > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-700">
                <Users size={13} />
                {event.total_entries} total entries
              </span>
            )}
            {event.height_cm_min != null && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-700">
                <Ruler size={13} />
                {event.height_cm_min === event.height_cm_max
                  ? `${event.height_cm_min} cm`
                  : `${event.height_cm_min}–${event.height_cm_max} cm`}
              </span>
            )}
            {prizeTotals.map(({ currency, value }) => (
              <span key={currency} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 rounded-full text-xs text-green-700 font-medium">
                <Trophy size={13} />
                {currencySymbols[currency] || currency}{formatPrizeSum(value)}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* Classes list */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-[320px] lg:max-w-[500px] mx-auto">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            Classes ({classes.length})
          </h2>

          {classes.length === 0 && !loading && (
            <p className="text-sm text-gray-500 text-center py-8">No classes found for this event.</p>
          )}

          <div className="space-y-3">
            {classes.map((cls) => (
              <button
                key={cls.unified_class_id}
                onClick={() => {
                  if (cls.unified_class_id) {
                    urlParams.navigateToCompetitionDetail(cls.unified_class_id);
                  }
                }}
                className="w-full bg-[#E7EEE3] rounded-[20px] shadow-sm p-4 sm:p-5 text-left transition-shadow hover:shadow-md"
              >
                <h4 className="text-sm font-bold text-[#132B64] leading-tight truncate">
                  {cls.unified_class_name || 'Unnamed Class'}
                </h4>

                <div className="mt-2 flex items-center gap-3 flex-wrap text-xs text-gray-600">
                  {cls.class_date && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(cls.class_date)}
                    </span>
                  )}
                  {cls.unified_height_cm != null && (
                    <span className="inline-flex items-center gap-1">
                      <Ruler size={12} />
                      {cls.unified_height_cm} cm
                    </span>
                  )}
                  {cls.unified_class_num_entries != null && (
                    <span className="inline-flex items-center gap-1">
                      <Users size={12} />
                      {cls.unified_class_num_entries} entries
                    </span>
                  )}
                </div>

                <div className="mt-1.5 flex items-center gap-3 flex-wrap text-xs text-gray-500">
                  {cls.unified_class_clear_round_rate != null && (
                    <span>Clear: {(cls.unified_class_clear_round_rate * 100).toFixed(0)}%</span>
                  )}
                  {cls.unified_class_avg_first_round_faults != null && (
                    <span>Avg faults: {cls.unified_class_avg_first_round_faults.toFixed(1)}</span>
                  )}
                  {[
                    { sym: '€', val: cls.total_prizes_eur },
                    { sym: '$', val: cls.total_prizes_usd },
                    { sym: '£', val: cls.total_prizes_gbp },
                    { sym: 'CHF ', val: cls.total_prizes_chf },
                    { sym: 'A$', val: cls.total_prizes_aud },
                    { sym: 'C$', val: cls.total_prizes_cad },
                  ].filter(p => p.val != null && p.val > 0).map(({ sym, val }) => (
                    <span key={sym} className="text-green-700 font-medium">
                      {sym}{val!.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
